import { validatePhoneNumberLength } from 'libphonenumber-js'
import React, { useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useModalState } from '~/atoms/ModalState'
import { useToastState } from '~/atoms/ToastState'
import { FontStyle } from '~/components/core/Text'
import { MangoButton } from '~/components/shared/MangoButton'
import { ContactSearchInput } from '~/components/specialized/ContactSearchInput/ContactSearchInput'
import { FILESIZE, MMSTypes } from '~/constants/config'
import { light, spacing } from '~/constants/theme'
import useMaxCharacterCount from '~/hooks/useMaxCharacterCount'
import MessageInput from '~/pages/Messaging/MessageInput'
import { EmojiData } from '~/types/EmojiMartTypes'
import { formatPhoneNumber, getValidNumber } from '~/utils/formatter'
import { fileToBase64 } from '~/utils/formatter'
import { useMessageGroupsViewModel } from '~/viewModels/Messaging/MessageGroupsViewModel'
import { useMessageThreadsViewModel } from '~/viewModels/Messaging/MessageThreadsViewModel'

import { ContactSourceType, MessageGroup } from '@mangovoice/types'

import { Text } from './core'
import { DropdownSelect } from './shared/Dropdown/DropdownSelect'
import { MangoModal } from './shared/MangoModal'

const ModalBodyWrapper = styled.div`
  width: 100%;
  margin-bottom: ${spacing.spacingMD};
`

const ModalDetailWrapper = styled.div`
  width: 100%;
`

const ContactAdminWrapper = styled.div`
  box-sizing: border-box;

  > p {
    margin-bottom: ${spacing.spacingXXL};
  }
`

const MangoATag = styled.a`
  text-decoration: none;
  color: ${light.primary};

  :hover {
    text-decoration: underline;
    color: ${light.primary};
  }
`

const Margin = styled.div`
  margin-bottom: ${spacing.spacingMD};
`

const NewThreadModal = () => {
  const [msgFile, setMsgFile] = useState<File | undefined>()
  const [fileUrl, setFileUrl] = useState<string>()
  const {
    messagingModal: [messagingModalState, setMessagingModalState],
  } = useModalState()

  const { t } = useTranslation()

  const { userMessageGroups, tryFetchUserMessageGroups } =
    useMessageGroupsViewModel()
  const groups = userMessageGroups ?? []

  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<MessageGroup>()
  const [phone, setPhone] = useState('')
  const [msgText, setMsgText] = useState('')

  const { tryGetOrCreateThread } = useMessageThreadsViewModel()
  const { hasExceededMaxCount } = useMaxCharacterCount(msgText.length)

  const {
    messagingToast: [, setMessagingToastState],
  } = useToastState()

  //on mount, try and fetch the latest message groups from the view model
  useEffect(() => {
    tryFetchUserMessageGroups()
  }, [])

  useEffect(() => {
    if (!selectedGroup && groups.length > 0) {
      setSelectedGroup(groups[0])
    }
  }, [groups, selectedGroup])

  const handleSelectGroup = (selectedId: number) => {
    const group = groups.find((g) => g.id === selectedId)
    setSelectedGroup(group)
    window.gtag('event', 'new_thread_modal_group_selected', {
      group_id: group?.id ?? 0,
      name: group?.name ?? '',
    })
  }

  const handleChangePhone = (e) => {
    const pNum = getValidNumber(e)
    if (pNum.length < 11) {
      setPhone(e)
    }
  }

  const onDrop: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => Promise<void> = useCallback(async (acceptedFiles, fileRejections) => {
    if (acceptedFiles[0]) {
      const dataURL = (await fileToBase64(acceptedFiles[0])) as string
      setMsgFile(acceptedFiles[0])
      setFileUrl(dataURL)
    }
    if (fileRejections[0]) {
      const errorCode = fileRejections[0].errors[0].code

      setMessagingToastState((currentMessagingState) => {
        return {
          ...currentMessagingState,
          messagingStatus: errorCode,
        }
      })
    }

    setIsOpenDialog(false)
    window.gtag('event', 'mms_file_picker_selected', {})
  }, [])
  const onFileDialogCancel = () => {
    setIsOpenDialog(false)
  }

  const isDraggable = !msgFile
  const { open, getInputProps } = useDropzone({
    accept: MMSTypes,
    disabled: !isDraggable,
    maxSize: FILESIZE.MAX_MMS,
    noDrag: true,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onFileDialogCancel,
  })

  const handleOpenAttach = () => {
    open()
    setIsOpenDialog(true)
    window.gtag('event', 'mms_file_picker_opened', {})
  }

  const handleSelectEmoji = (emoji: EmojiData) => {
    setMsgText((prevText) => prevText + emoji.native)
  }

  const handleCloseModal = () => {
    setSelectedGroup(groups[0])
    setPhone('')
    setMsgText('')
    setMessagingModalState({ open: false })
    setFileUrl(undefined)
    setMsgFile(undefined)
  }

  const handleClearInput = () => {
    setMsgText('')
    setFileUrl(undefined)
    setMsgFile(undefined)
  }

  const handleCreateNewThread = () => {
    const pNum = getValidNumber(phone)

    tryGetOrCreateThread({
      groupId: selectedGroup?.id ?? 0,
      externalPhoneNumber: pNum,
      name: pNum,
      message: {
        body: msgText,
        file: msgFile,
      },
    })
    window.gtag('event', 'new_thread_modal_send_pressed', {})

    handleCloseModal()
  }

  const displayValue = (group: MessageGroup) => {
    return ` ${group.name} - ${
      group.phone_numbers.find((number) => number.is_primary_sms)
        ?.number_for_display ?? ''
    }`
  }

  if (messagingModalState.number && phone !== messagingModalState.number) {
    setPhone(formatPhoneNumber(messagingModalState.number))
    setMessagingModalState({ ...messagingModalState, number: '' })
  }

  const pNum = getValidNumber(phone)
  const disabled =
    pNum.length < 10 ||
    (msgText === '' && msgFile === undefined) ||
    !selectedGroup ||
    hasExceededMaxCount
  const noGroups = groups.length === 0

  const isToNumberInvalid = Boolean(
    validatePhoneNumberLength(pNum ?? '', {
      defaultCountry: 'US',
    }),
  )
  return (
    <MangoModal
      isOpen={messagingModalState.open}
      header={
        noGroups
          ? t('newThreadModal.header.noGroupFound')
          : t('newThreadModal.header.newThread')
      }
      footer={
        <MangoButton disabled={disabled} onClick={handleCreateNewThread}>
          {t('newThreadModal.sendButtonText')}
        </MangoButton>
      }
      onClose={handleCloseModal}
    >
      {noGroups ? (
        <ModalBodyWrapper>
          <ContactAdminWrapper>
            <Text
              align="left"
              fontStyle={FontStyle.Normal}
              text={t('newThreadModal.contactText1')}
            />
            <Text
              align="left"
              fontStyle={FontStyle.Normal}
              text={t('newThreadModal.contactText2')}
            />
            <MangoATag
              href="https://support.mangovoice.com/what-is-a-messaging-group"
              target="_blank"
            >
              {t('newThreadModal.contactLinkText')}
            </MangoATag>
          </ContactAdminWrapper>
        </ModalBodyWrapper>
      ) : (
        <ModalBodyWrapper>
          <ModalDetailWrapper>
            <Margin>
              <DropdownSelect
                items={groups.map((rawGroup) => ({
                  value: rawGroup.id,
                  content: displayValue(rawGroup),
                }))}
                selectedValue={selectedGroup?.id}
                onSelect={(selectedId) => handleSelectGroup(selectedId)}
              />
            </Margin>
            <Margin>
              <ContactSearchInput
                scope="new_thread_modal"
                isInvalid={isToNumberInvalid}
                value={phone}
                onValueChanged={handleChangePhone}
                contactSourceTypeFilter={[ContactSourceType.Extensions]}
              />
            </Margin>
            <input {...getInputProps()} />
            <MessageInput
              text={msgText}
              onChange={(text) => setMsgText(text ?? '')}
              onSelect={handleSelectEmoji}
              isNewThread
              placeholder={t('newThreadModal.placeholder')}
              file={msgFile}
              fileURL={fileUrl}
              opened={isOpenDialog}
              disabled={disabled}
              onOpen={handleOpenAttach}
              onRemove={handleClearInput}
            />
          </ModalDetailWrapper>
        </ModalBodyWrapper>
      )}
    </MangoModal>
  )
}

export default NewThreadModal
